/**
 * @file AutofillUnitsDrawer.jsx
 * @module FlightRate / PackagingDetails
 * @description Slide-in drawer that lets the user autofill the packaging-type
 *   table without manual entry. Supports three input modes:
 *     1. XLSX / CSV file upload  — parsed client-side via the `xlsx` library.
 *     2. Image / screenshot upload — dimensions extracted by the AI backend
 *        endpoint `/extract_dms_screenshots`.
 *     3. Free-text paste — natural-language parser (e.g. "12 units 70x100x80cm 200kg").
 *   Parsed rows are previewed before being applied to the parent form via
 *   the `onApplyRows` callback.
 * @author rahul.kumar@team.sgate.in
 * @date 2026-06-29
 */

import React, { useState, useRef } from "react";
import { httpinstance } from "../../../../axios/api";

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  bg: "#FAF8F5",
  surface: "#FFFFFF",
  border: "#E8E3DC",
  orange: "#F5A300",
  orangeHover: "#E09200",
  orangeLight: "#FFF8EC",
  green: "#16A34A",
  greenBg: "#F0FDF4",
  greenBorder: "#BBF7D0",
  text: "#1C1917",
  textSub: "#78716C",
  textMuted: "#A8A29E",
  tabBg: "#EDE8E0",
  chipBorder: "#E8E3DC",
};

// ─── OptionsToggle ────────────────────────────────────────────────────────────
const OptionsToggle = ({ defaultTurnable, setDefaultTurnable, defaultStackable, setDefaultStackable, note }) => (
  <div style={{ marginTop: 16 }}>
    <p style={{ fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 8 }}>
      Default handling options
    </p>
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {[
        { label: "Turnable", val: defaultTurnable, set: setDefaultTurnable },
        { label: "Stackable", val: defaultStackable, set: setDefaultStackable },
      ].map(({ label, val, set }) => (
        <button
          key={label}
          type="button"
          onClick={() => set((v) => !v)}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "6px 14px", borderRadius: 20,
            border: val ? "none" : `1.5px solid ${C.border}`,
            background: val ? C.orange : C.surface,
            color: val ? "#fff" : C.textSub,
            fontSize: 12, fontWeight: 600, cursor: "pointer",
            transition: "all 0.15s",
          }}
        >
          {val ? (
            <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
              <path d="M2 7l4 4 6-7" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <span style={{ width: 11, height: 11, borderRadius: "50%", border: `1.5px solid ${C.border}`, display: "inline-block" }} />
          )}
          {label}
        </button>
      ))}
    </div>
    {note && <p style={{ fontSize: 11, color: C.textMuted, marginTop: 7 }}>{note}</p>}
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────
/**
 * AutofillUnitsDrawer
 * @description Slide-in panel (right-edge drawer) that parses package data from
 *   a file or free text and applies it to the packaging table in one click.
 *   All state is local and resets when the drawer closes via `resetDrawer()`.
 * @param {boolean}  open         - Whether the drawer is visible.
 * @param {Function} onClose      - Called when the user dismisses the drawer
 *                                  (backdrop click, × button, or after applying rows).
 * @param {Function} onApplyRows  - Receives the parsed rows array and writes them
 *                                  into the Formik packages field. Each row shape:
 *                                  { units, length, width, height, unit, weight,
 *                                    weightunit, turnable, stackable }
 */
const AutofillUnitsDrawer = ({ open, onClose, onApplyRows }) => {
  const [tab, setTab] = useState("text");
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [fileStatus, setFileStatus] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [textInput, setTextInput] = useState("");
  const [textStatus, setTextStatus] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [toast, setToast] = useState(null);
  const [applying, setApplying] = useState(false);
  const [dimUnit, setDimUnit] = useState("CM");
  const [defaultTurnable, setDefaultTurnable] = useState(true);
  const [defaultStackable, setDefaultStackable] = useState(true);
  const fileInputRef = useRef(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  // ── Parse rows from XLSX/CSV (array-of-arrays) ──────────────────────────────
  // Expected columns: Units | Length | Width | Height | Dimensions (CM/IN) |
  //   Weight | Weight measure (must be "KG") | Type (Unit/Total) |
  //   Turnable (true/false) | Stackable (true/false)
  /**
   * parseRowsFromData
   * @description Converts a 2-D array (first row = headers) from an XLSX/CSV
   *   sheet into a normalised rows array. Header names are matched with regex
   *   so the file column order is flexible; positional fallback is used when
   *   headers are absent or unrecognised.
   *
   *   Weight-measure handling: the "Weight measure" column must contain "KG".
   *   When the column is present and holds any other value (e.g. "LB", "TON"),
   *   that raw value is propagated to `weightunit` so that `validateRows` can
   *   surface a clear, row-level error instead of silently coercing to KG.
   *
   * @param {Array<Array<*>>} sheetData - Raw sheet data from xlsx.utils.sheet_to_json.
   * @returns {Array<object>} Normalised package rows. Rows with missing or
   *   non-numeric mandatory fields are silently skipped.
   */
  const parseRowsFromData = (sheetData) => {
    if (!sheetData || sheetData.length < 2) return [];
    const rows = [];
    const rawHeaders = sheetData[0];
    const headers = rawHeaders ? rawHeaders.map((h) => String(h || "").toLowerCase().trim()) : null;

    for (let i = 1; i < sheetData.length; i++) {
      const row = sheetData[i];
      if (!row || row.every((c) => c === null || c === undefined || c === "")) continue;

      let qty, length, width, height, dim, weight, weightMeasure, type, turnable, stackable;

      if (headers && headers.length >= 4) {
        const find = (patterns) => headers.findIndex((h) => patterns.some((p) => p.test(h)));
        const qtyIdx   = find([/^units?$|^qty$|^quantity$|^pcs?$|^pieces?$/]);
        const lenIdx   = find([/^length$|^len$|^\bl\b$/]);
        const widIdx   = find([/^width$|^wid$|^\bw\b$/]);
        const hgtIdx   = find([/^height$|^hgt$|^\bh\b$/]);
        const dimIdx   = find([/^dimensions?$|^dim$/]);
        const wtIdx    = find([/^weight$/]);
        const wtMIdx   = find([/^weight\s*measure$|^wt\s*measure$|^measure$/]);
        const typeIdx  = find([/^type$/]);
        const turnIdx  = find([/^turnable$|^turn$/]);
        const stackIdx = find([/^stackable$|^stack$/]);

        qty           = qtyIdx   >= 0 ? row[qtyIdx]   : row[0];
        length        = lenIdx   >= 0 ? row[lenIdx]   : row[1];
        width         = widIdx   >= 0 ? row[widIdx]   : row[2];
        height        = hgtIdx   >= 0 ? row[hgtIdx]   : row[3];
        dim           = dimIdx   >= 0 ? row[dimIdx]   : row[4];
        weight        = wtIdx    >= 0 ? row[wtIdx]    : row[5];
        weightMeasure = wtMIdx   >= 0 ? row[wtMIdx]   : row[6];
        type          = typeIdx  >= 0 ? row[typeIdx]  : row[7];
        turnable      = turnIdx  >= 0 ? row[turnIdx]  : row[8];
        stackable     = stackIdx >= 0 ? row[stackIdx] : row[9];
      } else {
        [qty, length, width, height, dim, weight, weightMeasure, type, turnable, stackable] = row;
      }

      const pQty = parseInt(qty, 10);
      const pLen = parseFloat(length);
      const pWid = parseFloat(width);
      const pHgt = parseFloat(height);
      const pWt  = parseFloat(weight);
      if (isNaN(pQty) || isNaN(pLen) || isNaN(pWid) || isNaN(pHgt) || isNaN(pWt)) continue;

      const dimUnit_  = /^in$/i.test(String(dim || "").trim()) ? "IN" : "CM";
      const isTotal   = /^total$/i.test(String(type || "").trim());

      // Normalise the Weight measure column value.
      const weightMeasureNorm = String(weightMeasure || "").trim().toUpperCase();
      // If the column is present but not "KG", propagate the raw invalid value
      // so validateRows surfaces a clear per-row error rather than coercing
      // silently to KG (which would produce wrong numbers for LB inputs).
      const wtUnit = weightMeasureNorm && weightMeasureNorm !== "KG"
        ? weightMeasureNorm
        : (isTotal ? "KG" : "KG/UNIT");

      const parseBool = (val, fb) => (val === undefined || val === null || val === "") ? fb : /^true$|^1$|^yes$/i.test(String(val).trim());
      const isTurnable  = parseBool(turnable,  defaultTurnable);
      const isStackable = parseBool(stackable, defaultStackable);

      rows.push({ units: pQty, length: pLen, width: pWid, height: pHgt, unit: dimUnit_, weight: pWt, weightunit: wtUnit, turnable: isTurnable, stackable: isStackable });
    }
    return rows;
  };

  // ── Validate rows ────────────────────────────────────────────────────────────
  /**
   * validateRows
   * @description Validates a parsed rows array before showing the preview.
   *   Enforces: positive integers for units, positive numbers for dimensions and
   *   weight, known unit strings, and consistency across rows (all rows must use
   *   the same dimension unit and weight type to avoid silent conversion errors).
   *
   *   Weight-measure errors show the actual invalid value (e.g. "got LB") so
   *   the user knows exactly which cell to fix without re-reading the template.
   *
   * @param {Array<object>} rows - Parsed rows to validate. Each row is expected
   *   to have the shape produced by `parseRowsFromData`.
   * @returns {{ valid: boolean, error?: string }} Result object; `error` is set
   *   when `valid` is false and contains a human-readable, row-labelled message.
   */
  const validateRows = (rows) => {
    if (!rows || rows.length === 0) return { valid: false, error: "File is empty or contains no data rows." };
    let firstDim = "", firstType = "";
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      const label = `Row ${i + 2}`;
      if (!r.units || isNaN(r.units) || r.units < 1 || !Number.isInteger(Number(r.units)))
        return { valid: false, error: `${label}: "Units" must be a whole number ≥ 1.` };
      if (!r.length || isNaN(r.length) || r.length <= 0)
        return { valid: false, error: `${label}: "Length" must be a positive number.` };
      if (!r.width || isNaN(r.width) || r.width <= 0)
        return { valid: false, error: `${label}: "Width" must be a positive number.` };
      if (!r.height || isNaN(r.height) || r.height <= 0)
        return { valid: false, error: `${label}: "Height" must be a positive number.` };
      if (!r.weight || isNaN(r.weight) || r.weight <= 0)
        return { valid: false, error: `${label}: "Weight" must be a positive number.` };
      if (!["CM", "IN"].includes(r.unit))
        return { valid: false, error: `${label}: "Dimensions" must be CM or IN.` };
      // Include the actual cell value in the error so the user knows exactly what to fix.
      if (!["KG/UNIT", "KG"].includes(r.weightunit))
        return { valid: false, error: `${label}: "Weight measure" must be KG (got "${r.weightunit}"). Only KG is supported.` };
      const typeLabel = r.weightunit === "KG" ? "Total" : "Unit";
      if (i === 0) { firstDim = r.unit; firstType = typeLabel; }
      else {
        if (r.unit !== firstDim)
          return { valid: false, error: `${label}: All rows must use the same dimension unit.` };
        if (typeLabel !== firstType)
          return { valid: false, error: `${label}: All rows must use the same weight type (Unit/Total).` };
      }
    }
    return { valid: true };
  };

  // ── File handler ─────────────────────────────────────────────────────────────
  /**
   * handleAutofillFileSelect
   * @description Entry point for both drag-drop and file-input selection.
   *   Detects file type and routes to either the AI image extraction endpoint
   *   (for image files) or the client-side XLSX parser (for spreadsheets/CSV).
   *   Updates fileStatus to "loading" → "ready" | "error" so the UI reflects
   *   parse progress without an explicit loading spinner component.
   * @param {File} selectedFile - The file chosen by the user.
   * @returns {Promise<void>}
   */
  const handleAutofillFileSelect = async (selectedFile) => {
    if (!selectedFile) return;
    setFile(selectedFile);
    setFileStatus("loading");
    setErrorMsg(null);
    setPreviewRows([]);

    const ext = selectedFile.name.split(".").pop().toLowerCase();
    const isImage = /^(jpg|jpeg|png|gif|bmp|webp|tiff?)$/.test(ext) || selectedFile.type.startsWith("image/");

    try {
      if (isImage) {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result.replace(/^data:[^;]+;base64,/, ""));
          reader.onerror = reject;
          reader.readAsDataURL(selectedFile);
        });
        const res = await httpinstance.post("/extract_dms_screenshots", { image: base64, fileName: selectedFile.name });
        const payload = res.data?.data ?? res.data;
        const arr = Array.isArray(payload?.shipmentDimensions)
          ? payload.shipmentDimensions
          : Array.isArray(payload)
            ? payload
            : [payload];
        const rows = arr
          .filter((r) => r && (r.length || r.width || r.height))
          .map((r) => ({
            units: parseInt(r.numberOfPieces || r.qty || 1, 10),
            length: parseFloat(r.length) || 0,
            width: parseFloat(r.width) || 0,
            height: parseFloat(r.height) || 0,
            unit: /^i/i.test(String(r.dimensionsUnit || "")) ? "IN" : "CM",
            weight: parseFloat(r.processedweightPerPiece || r.weightPerPiece || r.weight) || 0,
            weightunit: r.weightUnit === "KG" ? "KG/UNIT" : "KG/UNIT",
            turnable: r.turnable !== undefined ? r.turnable : defaultTurnable,
            stackable: r.stackable !== undefined ? r.stackable : defaultStackable,
          }));

        const v = validateRows(rows);
        if (!v.valid) { setFileStatus("error"); setErrorMsg(v.error); return; }
        setPreviewRows(rows);
        setFileStatus("ready");
      } else {
        const XLSX = await import("xlsx");
        const buffer = await selectedFile.arrayBuffer();
        const wb = XLSX.read(buffer, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const sheetData = XLSX.utils.sheet_to_json(ws, { header: 1 });
        const rows = parseRowsFromData(sheetData);
        const v = validateRows(rows);
        if (!v.valid) { setFileStatus("error"); setErrorMsg(v.error); return; }
        setPreviewRows(rows);
        setFileStatus("ready");
      }
    } catch (err) {
      setFileStatus("error");
      setErrorMsg(err?.response?.data?.message || err?.message || "Failed to process file.");
    }
  };

  // ── Text parser ──────────────────────────────────────────────────────────────
  /**
   * handleAutofillTextParse
   * @description Parses free-form text pasted by the user into package rows.
   *   Uses a two-pass strategy per line:
   *
   *   1. Compound-line regex — handles "N units LxWxH [unit] Wkg" patterns,
   *      including multiple groups on a single line (e.g. "5 pallets 120x100x150
   *      450kg 2 boxes 40x40x40 25kg"). Only kg is matched here; lb/pound is
   *      intentionally excluded because this path always assigns weightunit
   *      "KG/UNIT" and has no conversion logic — matching lb would silently
   *      produce wrong numbers (e.g. "450lb" stored as "450 KG/UNIT").
   *
   *   2. Natural-language fallback — extracts qty, dimensions, and weight from
   *      loosely formatted sentences when the compound pattern fails. This path
   *      also detects lb and sets weightunit "LB/UNIT" correctly.
   *
   *   Unit resolution: `dimUnit` (the CM/IN toggle the user selected) is always
   *   authoritative. Any unit annotations embedded in the text (e.g. "70cm×",
   *   "50in×") are intentionally ignored. This ensures the user's explicit unit
   *   selection always wins — pasted text may come from an external source with
   *   a different unit label, and overriding it silently would produce wrong data.
   *
   * @returns {void} — updates `previewRows` state on success or sets `errorMsg`
   *   on failure. No return value; side-effects only.
   */
  const handleAutofillTextParse = () => {
    if (!textInput.trim()) return;
    setTextStatus("loading");
    setErrorMsg(null);
    setPreviewRows([]);

    try {
      const lines = textInput.trim().split("\n").filter((l) => l.trim());
      const rows = [];

      for (const line of lines) {
        // Handle "5 pallets 120x100x150 450kg 2 boxes 40x40x40 25kg" (multiple groups per line)
        const lineStr = line.replace(/[×✕✗]/g, "x").replace(/\s+/g, " ").toLowerCase().trim();

        // Split compound lines into sub-entries: detect pattern "N <word> <dims> [unit] <weight>"
        const segments = [];
        // m[1]=qty  m[2]=L  m[3]=W  m[4]=H  m[5]=unit annotation (ignored — see below)  m[6]=weight
        // lb/pound deliberately excluded: this path always assigns weightunit
        // "KG/UNIT" and has no conversion logic, so matching lb would silently
        // produce wrong numbers (e.g. "450lb" stored as "450 KG/UNIT").
        const segRegex = /(\d+)\s+(?:units?|pcs?|pieces?|pallets?|boxes?|packages?|cartons?|crates?)?\s*(\d+\.?\d*)[\s]*[x×][\s]*(\d+\.?\d*)[\s]*[x×][\s]*(\d+\.?\d*)\s*(cm|in(?:ch(?:es?)?)?)?\s*(\d+\.?\d*)\s*kg(?:\s+|$)/gi;
        let m;
        let found = false;
        while ((m = segRegex.exec(lineStr)) !== null) {
          found = true;
          const qty = parseInt(m[1], 10);
          const l = parseFloat(m[2]);
          const w = parseFloat(m[3]);
          const h = parseFloat(m[4]);
          const wt = parseFloat(m[6]);
          // dimUnit is always authoritative — m[5] may carry a unit annotation
          // from the pasted text (e.g. "cm", "in") but the user's explicit toggle
          // selection must win. Ignoring m[5] prevents external text labels from
          // silently overriding the unit the user chose for this autofill session.
          const u = dimUnit;
          if (!isNaN(qty) && !isNaN(l) && !isNaN(w) && !isNaN(h) && !isNaN(wt)) {
            segments.push({ units: qty, length: l, width: w, height: h, weight: wt, unit: u });
          }
        }

        if (found) {
          segments.forEach((s) =>
            rows.push({ ...s, weightunit: "KG/UNIT", turnable: defaultTurnable, stackable: defaultStackable })
          );
          continue;
        }

        // Fallback: single-entry natural language parse
        const str = lineStr;
        let qty = 1;
        let rest = str;
        const qtyMatch = str.match(/^(\d+)\s*(?:pcs?|pieces?|units?|nos?\.?|pallets?|boxes?|packages?|cartons?)\s+/i);
        if (qtyMatch) { qty = parseInt(qtyMatch[1], 10); rest = str.slice(qtyMatch[0].length).trim(); }

        const dimMatch = rest.match(/(\d+\.?\d*)\s*(?:cm|in(?:ch(?:es?)?)?)?\s*x\s*(\d+\.?\d*)\s*(?:cm|in(?:ch(?:es?)?)?)?\s*x\s*(\d+\.?\d*)\s*(cm|in(?:ch(?:es?)?)?)?/i);
        if (!dimMatch) continue;

        const l = parseFloat(dimMatch[1]);
        const w = parseFloat(dimMatch[2]);
        const h = parseFloat(dimMatch[3]);
        // dimMatch[4] may hold a trailing unit annotation (e.g. "cm" from "80cm"),
        // but it is intentionally discarded — dimUnit is always authoritative so
        // the user's CM/IN toggle is never overridden by text content.
        const unitFromText = dimUnit;

        const weightMatch = rest.match(/(\d+\.?\d*)\s*(kg|lb(?:s?)|pound(?:s?)?)/i);
        if (!weightMatch) continue;

        const weight = parseFloat(weightMatch[1]);
        const weightunit = /lb|pound/i.test(weightMatch[2]) ? "LB/UNIT" : "KG/UNIT";
        if (isNaN(l) || isNaN(w) || isNaN(h) || isNaN(weight)) continue;

        rows.push({ units: qty, length: l, width: w, height: h, unit: unitFromText, weight, weightunit, turnable: defaultTurnable, stackable: defaultStackable });
      }

      if (rows.length === 0) {
        setTextStatus("error");
        setErrorMsg("Could not detect any dimensions. Try an example below.");
        return;
      }
      setPreviewRows(rows);
      setTextStatus("ready");
    } catch (err) {
      setTextStatus("error");
      setErrorMsg(err?.message || "Parse failed.");
    }
  };

  // ── Reset all drawer state to initial ────────────────────────────────────────
  const resetDrawer = () => {
    setTab("text");
    setDragging(false);
    setFile(null);
    setFileStatus(null);
    setPreviewRows([]);
    setTextInput("");
    setTextStatus(null);
    setErrorMsg(null);
    setToast(null);
    setApplying(false);
    setDimUnit("CM");
    setDefaultTurnable(true);
    setDefaultStackable(true);
  };

  // ── Apply rows ───────────────────────────────────────────────────────────────
  /**
   * applyParsedRows
   * @description Hands the validated preview rows to the parent form via
   *   `onApplyRows`, shows a brief confirmation toast, then resets state and
   *   closes the panel after a short delay.
   *
   *   Why the delay: React batches state updates within the same synchronous
   *   call, so setting `applying = true` and then calling `resetDrawer()` (which
   *   sets it back to false) in the same tick produces a single render where
   *   `applying` is still false — the "Applying…" button state is never shown.
   *   Deferring `resetDrawer` + `onClose` to a setTimeout breaks the batch,
   *   lets the "Applying…" state render, and gives the toast time to be seen.
   */
  const applyParsedRows = () => {
    if (!previewRows.length || !onApplyRows) return;
    setApplying(true);
    onApplyRows(previewRows);
    // Show row-count confirmation before the drawer closes.
    showToast(`${previewRows.length} row${previewRows.length > 1 ? "s" : ""} applied`);
    // 1200 ms: long enough for the toast to register, short enough to feel snappy.
    setTimeout(() => {
      resetDrawer();
      onClose();
    }, 1200);
  };

  // ── Template download ────────────────────────────────────────────────────────
  /**
   * downloadTemplate
   * @description Generates a minimal CSV template with the correct column headers
   *   and two sample rows, then triggers a browser download. Keeps users from
   *   guessing column names or order when preparing a file upload.
   *   Shows a brief toast so the user knows the download started — the browser's
   *   native download bar can be easy to miss on first use.
   */
  const downloadTemplate = () => {
    const csv =
      "Units,Length,Width,Height,Dimensions,Weight,Weight measure,Type,Turnable,Stackable\n" +
      "6,28,40,32,IN,90,KG,Unit,false,true\n" +
      "3,50,50,50,CM,180,KG,Total,false,true";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "autofill_template.csv";
    a.click();
    URL.revokeObjectURL(url);
    showToast("Template downloaded");
  };

  // ── Drag handlers ────────────────────────────────────────────────────────────
  const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = () => setDragging(false);
  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleAutofillFileSelect(f);
  };

  const switchTab = (t) => {
    setTab(t); setPreviewRows([]); setErrorMsg(null); setFileStatus(null); setTextStatus(null);
  };
  const clearPreview = () => { setPreviewRows([]); setFileStatus(null); setTextStatus(null); setErrorMsg(null); };

  const hasPreview = previewRows.length > 0;
  const status = tab === "file" ? fileStatus : textStatus;

  const EXAMPLES = [
    "12 units 70cmx100cmx80cm 200kg",
    "3 packages of 50 x 50 x 50 cm and a total weight of 180 kg",
    "5 pallets 120x100x150 450kg 2 boxes 40x40x40 25kg",
  ];

  return (
    <>
      {/* ── Backdrop ─────────────────────────────────────────── */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.32)",
          zIndex: 1200,
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 0.28s",
        }}
      />

      {/* ── Drawer panel ────────────────────────────────────── */}
      {/*
        Accessibility notes:
        • role="dialog" + aria-modal="true": tells screen readers this is a
          modal surface so they constrain virtual cursor to its contents.
        • aria-labelledby: associates the panel with its visible <h2> title.
        • aria-hidden + inert when closed: prevents keyboard users from tabbing
          into off-screen content and stops screen readers from announcing it.
          `inert` (HTML spec) suppresses all interaction (focus, pointer, find);
          `aria-hidden` covers the AT tree for browsers that don't yet support inert.
      */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="autofill-drawer-title"
        aria-hidden={!open}
        {...(!open ? { inert: "" } : {})}
        style={{
          position: "fixed", top: 0, bottom: 0,
          right: open ? 0 : "-500px",
          width: 460, maxWidth: "95vw",
          background: C.bg,
          zIndex: 1201,
          display: "flex", flexDirection: "column",
          boxShadow: "-6px 0 32px rgba(0,0,0,0.13)",
          transition: "right 0.34s cubic-bezier(.22,.61,.36,1)",
          fontFamily: "'Inter', 'Plus Jakarta Sans', system-ui, sans-serif",
          overflow: "hidden",
        }}
      >
        {/* ── Header ─────────────────────────────────────────── */}
        <div style={{ padding: "22px 24px 0", background: C.bg }}>
          {/* Title row */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 10 }}>
            <div>
              {/* id matches aria-labelledby on the dialog panel */}
              <h2 id="autofill-drawer-title" style={{ fontSize: 20, fontWeight: 700, color: C.text, margin: 0 }}>Autofill Units</h2>
            </div>
            <button
              onClick={onClose}
              type="button"
              style={{
                width: 30, height: 30, borderRadius: "50%",
                border: `1px solid ${C.border}`,
                background: C.surface, cursor: "pointer",
                fontSize: 16, color: C.textSub,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, marginTop: 2,
              }}
            >
              ×
            </button>
          </div>

          {/* AI subtitle */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18 }}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              background: C.orangeLight, border: `1px solid ${C.orange}`,
              borderRadius: 6, padding: "2px 7px",
              fontSize: 11, fontWeight: 700, color: C.orange,
            }}>
              ✦ AI
            </span>
            <span style={{ fontSize: 13, color: C.textSub }}>
              Skip manual entry — we read your file or text and fill the table.
            </span>
          </div>

          {/* Tab control */}
          <div style={{
            display: "flex",
            background: C.tabBg,
            borderRadius: 10,
            padding: 4,
            gap: 4,
          }}>
            {[
              { key: "file",  icon: "▤", label: "Upload file" },
              { key: "text",  icon: "✎", label: "Paste text" },
            ].map(({ key, icon, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => switchTab(key)}
                style={{
                  flex: 1, padding: "9px 0",
                  borderRadius: 7, border: "none",
                  cursor: "pointer",
                  fontWeight: tab === key ? 700 : 500,
                  fontSize: 13,
                  background: tab === key ? C.surface : "transparent",
                  color: tab === key ? C.text : C.textSub,
                  boxShadow: tab === key ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                  transition: "all 0.18s",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}
              >
                <span style={{ fontSize: 14 }}>{icon}</span>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Body ───────────────────────────────────────────── */}
        <div style={{ flex: 1, overflowY: "auto", padding: "18px 24px 0" }}>

          {/* ── FILE TAB ───────────────────────────────────── */}
          {tab === "file" && (
            <>
              {hasPreview ? (
                // Preview state
                <>
                  <div style={{
                    padding: "11px 14px", borderRadius: 8,
                    background: C.greenBg, border: `1px solid ${C.greenBorder}`,
                    color: C.green, fontSize: 13, fontWeight: 600, marginBottom: 16,
                  }}>
                    ✓ Detected {previewRows.length} row{previewRows.length > 1 ? "s" : ""} — review before applying.
                  </div>
                  <PreviewTable rows={previewRows} />
                </>
              ) : (
                <>
                  {/* Drop zone */}
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      border: `2px dashed ${dragging ? C.orange : C.border}`,
                      borderRadius: 10, padding: "28px 20px",
                      textAlign: "center", cursor: "pointer",
                      background: dragging ? C.orangeLight : C.surface,
                      transition: "all 0.18s", marginBottom: 16,
                    }}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls,.csv,image/*"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const f = e.target.files[0];
                        e.target.value = "";
                        if (f) handleAutofillFileSelect(f);
                      }}
                    />
                    <div style={{ fontSize: 26, marginBottom: 6 }}>
                      {fileStatus === "loading" ? "⏳" : fileStatus === "error" ? "❌" : "📂"}
                    </div>
                    {fileStatus === "loading" ? (
                      <p style={{ color: C.orange, fontWeight: 600, fontSize: 13 }}>Processing…</p>
                    ) : file ? (
                      <p style={{ color: C.text, fontWeight: 600, fontSize: 13 }}>{file.name} <span style={{ color: C.textMuted, fontWeight: 400 }}>— click to replace</span></p>
                    ) : (
                      <>
                        <p style={{ color: C.text, fontWeight: 600, fontSize: 13 }}>
                          Drop file here or <span style={{ color: C.orange }}>click to browse</span>
                        </p>
                        <div style={{ display: "flex", gap: 5, justifyContent: "center", marginTop: 10, flexWrap: "wrap" }}>
                          {[".XLSX", ".XLS", ".CSV", "Image"].map((f) => (
                            <span key={f} style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: C.bg, color: C.textSub, border: `1px solid ${C.border}` }}>{f}</span>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Error */}
                  {errorMsg && (
                    <div style={{ padding: "9px 13px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, color: "#DC2626", fontSize: 13, marginBottom: 14 }}>
                      {errorMsg}
                    </div>
                  )}

                  {/* Column format guide */}
                  <p style={{ fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 8 }}>Expected column format:</p>
                  <div style={{ overflowX: "auto", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, marginBottom: 10 }}>
                    <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ background: C.bg }}>
                          {["Units","Length","Width","Height","Dimensions","Weight","Wt.measure","Type","Turnable","Stackable"].map((h) => (
                            <th key={h} style={{ padding: "6px 8px", textAlign: "left", borderBottom: `1px solid ${C.border}`, color: C.textSub, fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ["6","28","40","32","IN","90","KG","Unit","false","true"],
                          ["3","50","50","50","CM","180","KG","Total","false","true"],
                        ].map((rv, ri) => (
                          <tr key={ri}>
                            {rv.map((v, ci) => (
                              <td key={ci} style={{ padding: "6px 8px", color: C.textSub }}>{v}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); downloadTemplate(); }}
                    style={{ fontSize: 12, color: C.orange, fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: 0, marginBottom: 4 }}
                  >
                    ⬇ Download template CSV
                  </button>

                  {/* Handling defaults */}
                  <OptionsToggle
                    defaultTurnable={defaultTurnable} setDefaultTurnable={setDefaultTurnable}
                    defaultStackable={defaultStackable} setDefaultStackable={setDefaultStackable}
                    note="Applied when Turnable / Stackable columns are missing in your file"
                  />
                </>
              )}
            </>
          )}

          {/* ── TEXT TAB ───────────────────────────────────── */}
          {tab === "text" && (
            <>
              {hasPreview ? (
                // Preview state
                <>
                  <div style={{
                    padding: "11px 14px", borderRadius: 8,
                    background: C.greenBg, border: `1px solid ${C.greenBorder}`,
                    color: C.green, fontSize: 13, fontWeight: 600, marginBottom: 16,
                  }}>
                    ✓ Detected {previewRows.length} row{previewRows.length > 1 ? "s" : ""} — review before applying.
                  </div>
                  <PreviewTable rows={previewRows} />
                </>
              ) : (
                <>
                  {/* Textarea */}
                  <textarea
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    placeholder={"Paste your details here…\ne.g. 12 units 70cmx100cmx80cm 200kg"}
                    rows={6}
                    style={{
                      width: "100%", padding: "12px 14px",
                      borderRadius: 10, border: `1.5px solid ${C.border}`,
                      fontSize: 13, fontFamily: "inherit",
                      resize: "vertical", outline: "none",
                      boxSizing: "border-box",
                      background: C.surface, color: C.text,
                      lineHeight: 1.65, transition: "border-color 0.15s",
                      marginBottom: 14,
                    }}
                    onFocus={(e) => (e.target.style.borderColor = C.orange)}
                    onBlur={(e) => (e.target.style.borderColor = C.border)}
                  />

                  {/* Error */}
                  {errorMsg && (
                    <div style={{ padding: "9px 13px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, color: "#DC2626", fontSize: 13, marginBottom: 14 }}>
                      {errorMsg}
                    </div>
                  )}

                  {/* Dimension unit selector */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                    <span style={{ fontSize: 13, color: C.textSub, fontWeight: 500 }}>Dimension unit:</span>
                    {["CM", "IN"].map((u) => (
                      <button
                        key={u}
                        type="button"
                        onClick={() => setDimUnit(u)}
                        style={{
                          padding: "5px 16px", borderRadius: 20,
                          border: dimUnit === u ? "none" : `1.5px solid ${C.border}`,
                          background: dimUnit === u ? C.orange : C.surface,
                          color: dimUnit === u ? "#fff" : C.textSub,
                          fontSize: 13, fontWeight: 700, cursor: "pointer",
                          transition: "all 0.15s",
                        }}
                      >
                        {u}
                      </button>
                    ))}
                  </div>

                  {/* Instructions */}
                  <p style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 10 }}>Instructions</p>
                  <p style={{ fontSize: 13, color: C.textSub, marginBottom: 10, lineHeight: 1.5 }}>
                    <strong style={{ color: C.text }}>1. Write your details</strong>, including all dimensions. Click to try an example:
                  </p>

                  {/* Example chips */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                    {EXAMPLES.map((ex, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setTextInput(ex)}
                        style={{
                          padding: "11px 14px", borderRadius: 8,
                          border: `1px solid ${C.chipBorder}`,
                          background: C.surface, cursor: "pointer",
                          fontSize: 13, color: C.text,
                          textAlign: "left", transition: "border-color 0.15s",
                          lineHeight: 1.4,
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.borderColor = C.orange)}
                        onMouseLeave={(e) => (e.currentTarget.style.borderColor = C.chipBorder)}
                      >
                        {ex}
                      </button>
                    ))}
                  </div>

                  <p style={{ fontSize: 12, color: C.textMuted, lineHeight: 1.6, marginBottom: 16 }}>
                    If no units of measurement are specified, we assume <strong style={{ color: C.textSub }}>centimetres</strong> and <strong style={{ color: C.textSub }}>kilograms</strong>.
                  </p>

                  {/* Handling defaults */}
                  <OptionsToggle
                    defaultTurnable={defaultTurnable} setDefaultTurnable={setDefaultTurnable}
                    defaultStackable={defaultStackable} setDefaultStackable={setDefaultStackable}
                  />
                </>
              )}
            </>
          )}

          {/* spacer */}
          <div style={{ height: 24 }} />
        </div>

        {/* ── Footer ─────────────────────────────────────────── */}
        <div style={{
          padding: "14px 24px",
          borderTop: `1px solid ${C.border}`,
          background: C.surface,
          display: "flex", gap: 10,
        }}>
          {hasPreview ? (
            <>
              {/* Back */}
              <button
                type="button"
                onClick={clearPreview}
                style={{
                  padding: "11px 20px", borderRadius: 8,
                  border: `1.5px solid ${C.border}`,
                  background: C.surface, color: C.textSub,
                  fontSize: 13, fontWeight: 600, cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                Back
              </button>
              {/* Apply */}
              <button
                type="button"
                onClick={applyParsedRows}
                disabled={applying}
                style={{
                  flex: 1, padding: "11px 0", borderRadius: 8,
                  border: "none",
                  background: applying ? "#FCD68A" : C.orange,
                  color: "#fff", fontSize: 13, fontWeight: 700,
                  cursor: applying ? "not-allowed" : "pointer",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => { if (!applying) e.currentTarget.style.background = C.orangeHover; }}
                onMouseLeave={(e) => { if (!applying) e.currentTarget.style.background = C.orange; }}
              >
                {applying ? "Applying…" : "Apply to shipment"}
              </button>
            </>
          ) : tab === "file" ? (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={status === "loading"}
              style={{
                flex: 1, padding: "11px 0", borderRadius: 8, border: "none",
                background: status === "loading" ? "#FCD68A" : C.orange,
                color: "#fff", fontSize: 13, fontWeight: 700,
                cursor: status === "loading" ? "not-allowed" : "pointer",
              }}
            >
              {status === "loading" ? "Processing…" : file ? "Re-upload file" : "Upload file"}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleAutofillTextParse}
              disabled={!textInput.trim() || status === "loading"}
              style={{
                flex: 1, padding: "11px 0", borderRadius: 8, border: "none",
                background: !textInput.trim() || status === "loading" ? C.tabBg : C.orange,
                color: !textInput.trim() || status === "loading" ? C.textMuted : "#fff",
                fontSize: 13, fontWeight: 700,
                cursor: !textInput.trim() ? "default" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                transition: "background 0.15s",
              }}
            >
              <span style={{ fontSize: 15 }}>✦</span>
              {status === "loading" ? "Parsing…" : "Autofill from text"}
            </button>
          )}
        </div>

        {/* ── Toast ──────────────────────────────────────────── */}
        {toast && (
          <div style={{
            position: "absolute", bottom: 76, left: "50%",
            transform: "translateX(-50%)",
            background: "#111827", color: "#fff",
            padding: "9px 18px", borderRadius: 8,
            fontSize: 13, fontWeight: 600,
            whiteSpace: "nowrap", zIndex: 10,
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          }}>
            ✓ {toast}
          </div>
        )}
      </div>
    </>
  );
};

// ── Preview table ─────────────────────────────────────────────────────────────
const PreviewTable = ({ rows }) => (
  <div style={{ borderRadius: 10, border: "1px solid #E8E3DC", overflow: "hidden", background: "#fff" }}>
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr style={{ background: "#FAF3E8" }}>
          <th style={thStyle}>Qty</th>
          <th style={thStyle}>Dimensions</th>
          <th style={{ ...thStyle, textAlign: "right" }}>Weight</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} style={{ borderTop: i > 0 ? "1px solid #F0EAE0" : "none" }}>
            <td style={tdStyle}>{row.units}</td>
            <td style={tdStyle}>
              {row.length}×{row.width}×{row.height}{" "}
              <span style={{ color: "#78716C" }}>{row.unit === "IN" ? "in" : "cm"}</span>
            </td>
            <td style={{ ...tdStyle, textAlign: "right" }}>
              {row.weight}{" "}
              <span style={{ color: "#78716C" }}>{row.weightunit === "LB/UNIT" ? "lb" : "kg"}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const thStyle = {
  padding: "10px 14px",
  textAlign: "left",
  fontSize: 12,
  fontWeight: 600,
  color: "#78716C",
  borderBottom: "1px solid #E8E3DC",
};
const tdStyle = {
  padding: "11px 14px",
  color: "#1C1917",
};

export default AutofillUnitsDrawer;
